import React, { useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Loader2, X, Image as ImageIcon } from "lucide-react";

/**
 * Reusable image uploader — posts to the backend which stores in Supabase
 * Storage and returns { path, public_url }. Emits the public_url upstream.
 *
 * Props:
 *   value      current image URL (controlled)
 *   onChange   (url|"", path|null) => void
 *   folder     Supabase Storage folder namespace (e.g. "services", "team")
 *   testId     data-testid prefix
 *   className  extra tailwind classes
 */
export default function ImageUploader({ value = "", onChange, folder = "uploads", testId = "img", className = "" }) {
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);

  const pick = () => inputRef.current?.click();

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error("Only JPG, PNG, WEBP are allowed");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File exceeds 5 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const r = await api.post(`/admin/upload/image?folder=${encodeURIComponent(folder)}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange?.(r.data.public_url, r.data.path);
      toast.success("Image uploaded");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const clear = () => onChange?.("", null);

  return (
    <div className={`space-y-3 ${className}`} data-testid={`${testId}-uploader`}>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={onFile}
        hidden
        data-testid={`${testId}-file-input`}
      />
      {value ? (
        <div className="relative inline-block group">
          <img src={value} alt="preview"
               className="max-h-40 rounded-md border border-navy/15 object-cover"
               data-testid={`${testId}-preview`} />
          <button
            type="button"
            onClick={clear}
            className="absolute top-1 end-1 bg-navy/85 text-cream rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
            data-testid={`${testId}-clear`}
            title="Remove image"
          >
            <X size={12} />
          </button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-navy/20 rounded-md p-6 text-center text-navy/50 bg-cream/30">
          <ImageIcon size={26} className="mx-auto opacity-60" />
          <div className="text-xs mt-2">JPG · PNG · WEBP · max 5 MB</div>
        </div>
      )}
      <div className="flex gap-2 items-center">
        <button
          type="button"
          onClick={pick}
          disabled={busy}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-sm border border-gold/60 text-navy hover:bg-gold/10 text-sm disabled:opacity-40"
          data-testid={`${testId}-pick`}
        >
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
          {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
        </button>
      </div>
    </div>
  );
}
